import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import { Schema as S } from "effect";
/**
 * A person changing their own private address (PMO-373). The address is the
 * person's own: these routes act only for the signed-in caller, and nothing
 * here is readable by anybody else.
 */
/** The switch is off, or the account holds no person with addresses. */
export const PrivateEmailUnavailable = S.Struct({
    code: S.Literal("NOT_FOUND"),
    message: S.String,
});
/** The caller's confirmed private address, and a new one waiting for its link. */
export const PrivateEmail = S.Struct({
    current: S.NullOr(S.String),
    pending: S.NullOr(S.String),
});
export const ChangePrivateEmailPayload = S.Struct({
    email: S.NonEmptyString,
    /** The language of the confirmation mail. */
    language: S.Literal("de-DE", "en-US"),
});
/**
 * `confirmation_sent`: a link went to the new address, which the person has
 * to confirm. `unchanged`: it already is their confirmed private address.
 */
export const ChangePrivateEmailResult = S.Struct({
    outcome: S.Literal("confirmation_sent", "unchanged"),
});
/** Not an address the identity layer can key. */
export const PrivateEmailInvalid = S.Struct({
    _tag: S.Literal("PrivateEmailInvalid"),
});
/**
 * Somebody else holds the address, or it is under manual resolution. The
 * answer is the same either way and names nobody.
 */
export const PrivateEmailTaken = S.Struct({
    _tag: S.Literal("PrivateEmailTaken"),
});
/** The address is one of the caller's own work addresses. */
export const PrivateEmailIsWorkAddress = S.Struct({
    _tag: S.Literal("PrivateEmailIsWorkAddress"),
});
/**
 * The person, or the address, has had its share of confirmation mails for
 * now. Answered like the other rate limits (`WebSignupRateLimited`,
 * `LoginMethodsRateLimited`): a 429 with the seconds until a retry can
 * succeed. Nothing was written or mailed.
 */
export const PrivateEmailRateLimited = S.Struct({
    _tag: S.Literal("PrivateEmailRateLimited"),
    retryAfterSeconds: S.Number,
});
export const GetPrivateEmail = HttpApiEndpoint.get("getPrivateEmail", "/session/private-email")
    .addSuccess(S.Struct({ data: PrivateEmail }))
    .addError(PrivateEmailUnavailable, { status: 404 });
export const ChangePrivateEmail = HttpApiEndpoint.post("changePrivateEmail", "/session/private-email")
    .setPayload(ChangePrivateEmailPayload)
    .addSuccess(S.Struct({ data: ChangePrivateEmailResult }))
    .addError(PrivateEmailUnavailable, { status: 404 })
    .addError(PrivateEmailInvalid, { status: 400 })
    .addError(PrivateEmailTaken, { status: 409 })
    .addError(PrivateEmailIsWorkAddress, { status: 409 })
    .addError(PrivateEmailRateLimited, { status: 429 });
export const PrivateEmailTokenPayload = S.Struct({ token: S.NonEmptyString });
/**
 * What the link's page shows before anybody decides: the address being
 * confirmed, and the requesting person's work address, masked. Anything else
 * is how the change ended. Read-only.
 */
export const PrivateEmailPreviewResult = S.Union(S.Struct({
    outcome: S.Literal("open"),
    email: S.String,
    requester: S.NullOr(S.String),
}), S.Struct({
    outcome: S.Literal("already_confirmed", "expired", "superseded", "cancelled", "invalid"),
}));
export const PrivateEmailConfirmResult = S.Struct({
    outcome: S.Literal("confirmed", "already_confirmed", "expired", "superseded", "cancelled", "invalid"),
});
export const PrivateEmailCancelResult = S.Struct({
    outcome: S.Literal("cancelled", "already_confirmed", "superseded", "invalid"),
});
/**
 * `sent`: a fresh link went to the same address and the old one stopped
 * working. Anything else is how the change had already ended; `invalid`
 * also covers an address that is no longer the person's to take.
 */
export const PrivateEmailRenewResult = S.Struct({
    outcome: S.Literal("sent", "already_confirmed", "superseded", "cancelled", "invalid"),
});
/**
 * Previewing, confirming, cancelling and renewing a private-address change
 * from the mailed link. Public: the token is the proof, so the group carries
 * no principal middleware. Every route is a POST with the token in the body,
 * so a link fetched by a browser or a mail scanner confirms nothing.
 */
export const privateEmailConfirmationGroup = HttpApiGroup.make("privateEmailConfirmation")
    .add(HttpApiEndpoint.post("preview", "/identity/private-email/preview")
    .setPayload(PrivateEmailTokenPayload)
    .addSuccess(PrivateEmailPreviewResult)
    .addError(PrivateEmailUnavailable, { status: 404 }))
    .add(HttpApiEndpoint.post("confirm", "/identity/private-email/confirm")
    .setPayload(PrivateEmailTokenPayload)
    .addSuccess(PrivateEmailConfirmResult)
    .addError(PrivateEmailUnavailable, { status: 404 }))
    .add(HttpApiEndpoint.post("cancel", "/identity/private-email/cancel")
    .setPayload(PrivateEmailTokenPayload)
    .addSuccess(PrivateEmailCancelResult)
    .addError(PrivateEmailUnavailable, { status: 404 }))
    .add(HttpApiEndpoint.post("renew", "/identity/private-email/renew")
    .setPayload(PrivateEmailTokenPayload)
    .addSuccess(PrivateEmailRenewResult)
    .addError(PrivateEmailUnavailable, { status: 404 })
    .addError(PrivateEmailRateLimited, { status: 429 }));
/**
 * The outbox message the products refresh their copy of a person's private
 * address from. It names only the person; the consumer reads the address the
 * identity layer holds when it runs, so a replayed or late message never
 * writes an older one.
 */
export const PRIVATE_EMAIL_CHANGED_TYPE = "identity.private-email-changed";
//# sourceMappingURL=private-email.js.map